/** Pure H-1B max-out planning estimates. Not a legal determination. */

export type PermStage =
  | 'not_started'
  | 'pwd_or_recruitment'
  | 'perm_pending'
  | 'perm_approved'
  | 'i140_pending'
  | 'i140_approved'

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical'

export type CalculatorInput = {
  startDate: string // YYYY-MM-DD
  previousHlDays: number
  recaptureDays: number
  permStage: PermStage
}

export type CalculatorResult = {
  maxOutDate: Date
  maxOutWithRecapture: Date
  daysRemaining: number
  monthsRemaining: number
  risk: RiskLevel
  riskLabel: string
  recommendedPermStart: Date
  recommendedDiscussionStart: Date
  guidance: string
  extensionNote: string | null
}

export const PERM_STAGE_OPTIONS: { value: PermStage; label: string }[] = [
  { value: 'not_started', label: 'PERM has not started' },
  { value: 'pwd_or_recruitment', label: 'PWD or recruitment in progress' },
  { value: 'perm_pending', label: 'PERM filed and pending' },
  { value: 'perm_approved', label: 'PERM approved' },
  { value: 'i140_pending', label: 'I-140 filed and pending' },
  { value: 'i140_approved', label: 'I-140 approved' },
]

const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'Lower risk',
  medium: 'Medium risk',
  high: 'High risk',
  critical: 'Critical',
}

/** Parse an HTML date input value as a local calendar date (avoids UTC shift). */
export function parseLocalDate(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null
  return date
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  next.setDate(next.getDate() + days)
  return next
}

export function addYears(date: Date, years: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  next.setFullYear(next.getFullYear() + years)
  return next
}

export function startOfToday(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

export function daysBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime()
  return Math.round(ms / (1000 * 60 * 60 * 24))
}

export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatRemaining(days: number): string {
  if (days < 0) {
    const abs = Math.abs(days)
    if (abs < 30) return `${abs} day${abs === 1 ? '' : 's'} past max-out`
    const months = Math.floor(abs / 30)
    return `About ${months} month${months === 1 ? '' : 's'} past max-out`
  }
  if (days === 0) return 'Max-out is today'
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} remaining`
  const years = Math.floor(days / 365)
  const months = Math.floor((days % 365) / 30)
  if (years > 0 && months > 0) {
    return `${years} year${years === 1 ? '' : 's'}, ${months} month${months === 1 ? '' : 's'} remaining`
  }
  if (years > 0) return `${years} year${years === 1 ? '' : 's'} remaining`
  if (months > 0) return `About ${months} month${months === 1 ? '' : 's'} remaining`
  return `${days} days remaining`
}

function baseRiskFromMonths(monthsRemaining: number): RiskLevel {
  if (monthsRemaining > 24) return 'low'
  if (monthsRemaining > 12) return 'medium'
  if (monthsRemaining > 6) return 'high'
  return 'critical'
}

function elevateRisk(risk: RiskLevel): RiskLevel {
  if (risk === 'low') return 'medium'
  if (risk === 'medium') return 'high'
  return 'critical'
}

function reduceRisk(risk: RiskLevel): RiskLevel {
  if (risk === 'critical') return 'high'
  if (risk === 'high') return 'medium'
  if (risk === 'medium') return 'low'
  return 'low'
}

function adjustRiskForStage(risk: RiskLevel, stage: PermStage, monthsRemaining: number): RiskLevel {
  if (stage === 'i140_approved') {
    // Approved I-140 often supports AC21 extensions when visa numbers are unavailable
    if (monthsRemaining > 6) return 'low'
    return reduceRisk(risk)
  }
  if (stage === 'i140_pending' || stage === 'perm_approved') {
    return reduceRisk(risk)
  }
  if (stage === 'perm_pending' && monthsRemaining > 6) {
    return reduceRisk(risk)
  }
  if (stage === 'not_started' && monthsRemaining <= 24) {
    return elevateRisk(risk)
  }
  if (stage === 'pwd_or_recruitment' && monthsRemaining <= 12) {
    return elevateRisk(risk)
  }
  return risk
}

function buildGuidance(risk: RiskLevel, stage: PermStage, monthsRemaining: number): string {
  if (stage === 'i140_approved') {
    return 'With an approved I-140, you may have a path to extend H-1B beyond six years if an immigrant visa number is unavailable. Confirm Visa Bulletin status and extension strategy with counsel.'
  }
  if (stage === 'i140_pending' || stage === 'perm_approved') {
    return 'You are further along in the green card process. Track I-140 adjudication closely and prepare the H-1B extension package before max-out.'
  }
  if (stage === 'perm_pending') {
    return 'Monitor DOL processing times and prepare the I-140 package in advance. Check whether the PERM filing date will support a one-year AC21 extension under the 365-day rule.'
  }
  if (monthsRemaining > 24) {
    return 'This is a good window to discuss employer sponsorship, job requirements, and EB-2 vs EB-3 strategy so PWD and PERM can start on time.'
  }
  if (monthsRemaining > 12) {
    return 'Aim to move into prevailing wage and recruitment soon. Delays at this stage often push PERM too close to max-out.'
  }
  if (monthsRemaining > 6) {
    return 'Time is limited. Confirm where PERM stands, document recapture days, and evaluate backup options such as transfer, change of status, or consular planning.'
  }
  if (risk === 'critical' || stage === 'not_started') {
    return 'Act urgently. Do not rely on a last-minute PERM filing alone. Review recapture, current I-94, extension eligibility, and alternative pathways with an attorney.'
  }
  return 'Review your timeline with counsel so PERM, I-140, and any AC21 extension basis stay aligned with your max-out date.'
}

function buildExtensionNote(stage: PermStage): string | null {
  if (stage === 'i140_approved') {
    return 'An approved I-140 may support H-1B extensions in up to three-year increments when an immigrant visa number is unavailable due to per-country limits.'
  }
  if (
    stage === 'perm_pending' ||
    stage === 'perm_approved' ||
    stage === 'i140_pending'
  ) {
    return 'If at least 365 days have passed since PERM or I-140 filing by the requested start of the next H-1B period, a one-year extension beyond six years may be available under AC21.'
  }
  return null
}

export function calculateMaxOut(input: CalculatorInput): CalculatorResult | null {
  const start = parseLocalDate(input.startDate)
  if (!start) return null

  const previousHlDays = Math.max(0, Math.floor(input.previousHlDays) || 0)
  const recaptureDays = Math.max(0, Math.floor(input.recaptureDays) || 0)

  // 6 calendar years from the start of the counting period, reduced by prior H/L time,
  // then extended by documented recapture days (if requested in a later petition).
  const maxOutDate = addDays(addYears(start, 6), -previousHlDays)
  const maxOutWithRecapture = addDays(maxOutDate, recaptureDays)

  const today = startOfToday()
  const daysRemaining = daysBetween(today, maxOutWithRecapture)
  const monthsRemaining = daysRemaining / 30.4375

  let risk = baseRiskFromMonths(monthsRemaining)
  risk = adjustRiskForStage(risk, input.permStage, monthsRemaining)

  return {
    maxOutDate,
    maxOutWithRecapture,
    daysRemaining,
    monthsRemaining,
    risk,
    riskLabel: RISK_LABELS[risk],
    recommendedPermStart: addDays(maxOutWithRecapture, -Math.round(24 * 30.4375)),
    recommendedDiscussionStart: addDays(maxOutWithRecapture, -Math.round(30 * 30.4375)),
    guidance: buildGuidance(risk, input.permStage, monthsRemaining),
    extensionNote: buildExtensionNote(input.permStage),
  }
}
